/**
 * Limits and messages for the Mermaid renderer (FRIDAY-67).
 *
 * The limits are checked BEFORE the source reaches Mermaid. A pathological
 * diagram (a huge graph, a config directive) must never be able to hang
 * the tab it is rendered in.
 */

/** Longest source we accept for a single diagram. */
export const MAX_SOURCE_LENGTH = 20_000

/** Most edges we accept in a single diagram. */
export const MAX_EDGES = 500

export type MermaidSourceCheck = { ok: true } | { ok: false; message: string }

/**
 * `%%{init: …}%%` is Mermaid's in-diagram configuration channel. Friday
 * decides how a diagram looks (theme, colours, limits), so a diagram is
 * not allowed to change that. Mermaid itself only blocks a handful of
 * keys (`securityLevel`, `maxTextSize`, …) — `theme` and `themeVariables`
 * are still writable from a diagram, so we refuse the whole directive.
 */
const DIRECTIVE = /%%\{[\s\S]*?\}%%/

/** Checks a diagram source before it is handed to the renderer. */
export function checkMermaidSource(source: string): MermaidSourceCheck {
  if (!source.trim()) {
    return { ok: false, message: 'This diagram is empty.' }
  }
  if (source.length > MAX_SOURCE_LENGTH) {
    return {
      ok: false,
      message: `This diagram is ${source.length.toLocaleString()} characters long — the limit is ${MAX_SOURCE_LENGTH.toLocaleString()}. Shorten it and it will render.`,
    }
  }
  if (DIRECTIVE.test(source)) {
    return {
      ok: false,
      message:
        'Mermaid configuration directives (%%{init:…}%%) are not allowed. Friday decides how a diagram looks.',
    }
  }
  return { ok: true }
}
